import { ExecutionTask, UITask } from '../types';
import { TaskStatus } from '@/types/tasks';

export type UiTaskAction =
	| { type: 'updateStatus'; id: string; status: TaskStatus; progress?: number }
	| { type: 'updateId'; id: string; newId: string }
	| { type: 'updateMetadata'; id: string; patch: Record<string, unknown> }
	| { type: 'updateExecution'; id: string; patch: Partial<ExecutionTask> }
	| { type: 'addMany'; items: UITask[] }
	| { type: 'replaceAll'; items: UITask[] };

/**
 * Pure reducer for uiTasks. Keeps all transformations centralized and testable.
 */
export function uiTaskReducer(state: UITask[], action: UiTaskAction): UITask[] {
	switch (action.type) {
		case 'updateStatus': {
			const { id, status, progress } = action;
			let matched = false;
			return state.map((t) => {
				const isTarget = t.id === id && !matched;
				if (!isTarget) return t;
				matched = true; // ensure we only update one matching entry when duplicates exist

				return {
					...t,
					execution: {
						...t.execution,
						apiTask: {
							...t.execution.apiTask,
							status,
							...(progress !== undefined ? { progress } : {}),
						},
					},
				};
			});
		}
		case 'updateId': {
			const { id, newId } = action;
			let matched = false;
			return state.map((t) => {
				const isTarget = t.id === id && !matched;
				if (!isTarget) return t;
				matched = true;
				return {
					...t,
					execution: {
						...t.execution,
						apiTask: {
							...t.execution.apiTask,
							id: newId,
						},
					},
				};
			});
		}
		case 'updateMetadata': {
			const { id, patch } = action;
			let matched = false;
			return state.map((t) => {
				const isTarget = t.id === id && !matched;
				if (!isTarget) return t;
				matched = true;
				return {
					...t,
					execution: {
						...t.execution,
						apiTask: {
							...t.execution.apiTask,
							metadata: {
								...t.execution.apiTask.metadata,
								...patch,
							},
						},
					},
				};
			});
		}
		case 'updateExecution': {
			const { id, patch } = action;
			let matched = false;
			return state.map((t) => {
				const isTarget = t.id === id && !matched;
				if (!isTarget) return t;
				matched = true;
				return { ...t, execution: { ...t.execution, ...patch } };
			});
		}
		case 'addMany': {
			if (!action.items?.length) return state;
			return [...state, ...action.items];
		}
		case 'replaceAll': {
			return action.items ? [...action.items] : [];
		}
		default:
			return state;
	}
}

// Optional action creators for clarity in the provider
export const actions = {
	updateStatus: (id: string, status: TaskStatus, progress?: number): UiTaskAction => ({
		type: 'updateStatus',
		id,
		status,
		progress,
	}),
	updateId: (id: string, newId: string): UiTaskAction => ({ type: 'updateId', id, newId }),
	updateMetadata: (id: string, patch: Record<string, unknown>): UiTaskAction => ({
		type: 'updateMetadata',
		id,
		patch,
	}),
	updateExecution: (id: string, patch: Partial<ExecutionTask>): UiTaskAction => ({
		type: 'updateExecution',
		id,
		patch,
	}),
	addMany: (items: UITask[]): UiTaskAction => ({ type: 'addMany', items }),
	replaceAll: (items: UITask[]): UiTaskAction => ({ type: 'replaceAll', items }),
};
